
import React, { useState, useEffect } from 'react';
import {
    uploadFile,
    getFileUrl,
    createFollowUpSequence,
    getFollowUpSequence,
    updateFollowUpSequence,
    deleteFile
} from '../services/api.ts';
import {
    Trash2,
    Edit2,
    Plus,
    Play,
    Calendar,
    Clock,
    MessageCircle,
    Check,
    X,
    Image,
    Video,
    Music,
    Type,
    ChevronRight,
    Save,
    AlertCircle,
    Smartphone,
    ArrowRight,
    MoreVertical,
    Layout,
    Maximize2
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

// ---------------------------
// Types
// ---------------------------
interface MensagemLocal {
    id?: number;
    type: 'text' | 'image' | 'audio' | 'video';
    content: string | File | any;
}

interface PassoLocal {
    id?: number;
    step_number: number;
    send_after: number;
    send_after_unit: 'days' | 'hours' | 'minutes';
    messages: MensagemLocal[];
}

interface MensagemAPI {
    id?: number;
    type: string;
    content: string;
}

interface PassoAPI {
    id?: number;
    step_number: number;
    send_after: number;
    send_after_unit: string;
    messages: MensagemAPI[];
}

const MAX_PASSOS = 10;
const MAX_MENSAGENS_POR_PASSO = 3;

const LIMITE_ARQUIVOS = {
    image: 2 * 1024 * 1024,
    audio: 5 * 1024 * 1024,
    video: 5 * 1024 * 1024,
};

const timeUnits = [
    { value: 'minutes', label: 'Minutos' },
    { value: 'hours', label: 'Horas' },
    { value: 'days', label: 'Dias' }
];

const messageTypes = [
    { value: 'text', label: 'Texto', icon: Type },
    { value: 'image', label: 'Imagem', icon: Image },
    { value: 'audio', label: 'Áudio', icon: Music },
    { value: 'video', label: 'Vídeo', icon: Video }
];

function isFile(value: any): boolean {
    return (
        typeof value === 'object' &&
        value !== null &&
        'name' in value &&
        'size' in value &&
        'type' in value
    );
}

// ---------------------------
// Mobile Preview Component
// ---------------------------
const MobilePreview = ({ step }: { step: PassoLocal | null }) => {
    const { isDark } = useTheme();

    if (!step) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-50">
                <Smartphone className="w-12 h-12 mb-4" />
                <p className="text-sm">Selecione um passo para visualizar</p>
            </div>
        );
    }

    return (
        <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-900 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl overflow-hidden ring-4 ring-gray-900/10">
            {/* Notch */}
            <div className="absolute top-0 w-full h-8 bg-gray-800 flex justify-center z-10 rounded-b-xl px-8">
                <div className="w-16 h-4 bg-black rounded-b-lg"></div>
            </div>

            {/* Screen Content */}
            <div className="w-full h-full bg-[#E5DDD5] dark:bg-[#0b141a] overflow-y-auto flex flex-col pt-12 pb-4 px-2 custom-scrollbar relative">
                {/* Background Pattern Overlay */}
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                    style={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")' }}>
                </div>

                {/* Date bubble */}
                <div className="flex justify-center mb-4 z-0">
                    <span className="bg-[#E1F3FB] dark:bg-[#1F2C34] text-gray-500 dark:text-gray-400 text-[10px] px-2 py-1 rounded shadow-sm">
                        Hoje
                    </span>
                </div>

                {/* Messages */}
                <div className="space-y-2 z-0">
                    {step.messages.map((msg, idx) => (
                        <div key={idx} className="flex flex-col items-start max-w-[85%]">
                            <div className={`rounded-lg px-3 py-2 shadow-sm text-sm relative ${'bg-white dark:bg-[#202C33] text-gray-800 dark:text-gray-100 rounded-tl-none'
                                }`}>
                                {msg.type === 'text' && (
                                    <p className="whitespace-pre-wrap leading-snug">
                                        {typeof msg.content === 'string' ? msg.content : '...'}
                                    </p>
                                )}

                                {msg.type === 'image' && (
                                    <div className="mb-1 rounded overflow-hidden relative">
                                        <div className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 h-32 w-48 text-gray-400 text-xs">
                                            <Image className="w-6 h-6 mb-1" />
                                        </div>
                                    </div>
                                )}

                                {msg.type === 'audio' && (
                                    <div className="flex items-center gap-2 min-w-[150px]">
                                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                            <Play className="w-4 h-4 text-gray-500 dark:text-gray-300 fill-current ml-0.5" />
                                        </div>
                                        <div className="h-1 bg-gray-300 dark:bg-gray-600 flex-1 rounded-full"></div>
                                    </div>
                                )}

                                {msg.type === 'video' && (
                                    <div className="mb-1 rounded overflow-hidden relative">
                                        <div className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 h-32 w-48 text-gray-400 text-xs">
                                            <Video className="w-6 h-6 mb-1" />
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end items-center gap-1 mt-1 opacity-60">
                                    <span className="text-[10px]">10:30</span>
                                </div>
                            </div>
                        </div>
                    ))}

                    {step.messages.length === 0 && (
                        <div className="flex justify-center mt-8 opacity-50">
                            <span className="text-xs bg-gray-200 dark:bg-gray-800 px-3 py-1 rounded-full text-gray-500">
                                Nenhuma mensagem
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Input Area Mock */}
            <div className="absolute bottom-0 w-full bg-[#F0F2F5] dark:bg-[#202C33] px-2 py-2 flex items-center gap-2 z-20">
                <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                <div className="flex-1 h-8 bg-white dark:bg-[#2A3942] rounded-full px-4 text-xs flex items-center text-gray-400">
                    Mensagem
                </div>
                <div className="w-8 h-8 rounded-full bg-[#00A884] flex items-center justify-center">
                    <MoreVertical className="w-4 h-4 text-white" />
                </div>
            </div>
        </div>
    );
};

// ---------------------------
// Main Component
// ---------------------------
const FollowUpModern: React.FC = () => {
    const { isDark } = useTheme();

    // State from FollowUp-v2
    const [passos, setPassos] = useState<PassoLocal[]>([]);
    const [selectedStepId, setSelectedStepId] = useState<number | null>(null);

    const [erro, setErro] = useState<string | null>(null);
    const [sucesso, setSucesso] = useState<string | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [hasSequence, setHasSequence] = useState(false);
    const [savedStatus, setSavedStatus] = useState<string | null>(null); // 'saving' | 'saved' | 'error'

    // Load Config
    useEffect(() => {
        const clinicIdStr = localStorage.getItem('clinic_id');
        if (!clinicIdStr) return;
        carregarConfigExistente(Number(clinicIdStr));
    }, []);

    const carregarConfigExistente = async (clinicId: number) => {
        try {
            setCarregando(true);
            const seq = await getFollowUpSequence(clinicId);

            if (seq === null) {
                setHasSequence(false);
                setPassos([]);
            } else {
                setHasSequence(true);
                const passosConvertidos: PassoLocal[] = seq.steps.map((step: any) => ({
                    id: step.id,
                    step_number: step.step_number,
                    send_after: step.send_after,
                    send_after_unit: step.send_after_unit as 'days' | 'hours' | 'minutes',
                    messages: step.messages.map((msg: any) => ({
                        id: msg.id,
                        type: msg.type,
                        content: msg.content,
                    })),
                }));

                // Sort by step number just in case
                passosConvertidos.sort((a, b) => a.step_number - b.step_number);
                setPassos(passosConvertidos);

                // Select first step by default if exists
                if (passosConvertidos.length > 0) {
                    setSelectedStepId(passosConvertidos[0].step_number);
                }
            }
        } catch (error) {
            console.error(error);
            setErro('Erro ao carregar configuração');
        } finally {
            setCarregando(false);
        }
    };

    // Logic Functions
    const adicionarPasso = () => {
        if (passos.length >= MAX_PASSOS) return;

        const novoPasso: PassoLocal = {
            step_number: passos.length + 1,
            send_after: 1,
            send_after_unit: 'days',
            messages: [],
        };

        // Optimistic update
        const newPassos = [...passos, novoPasso];
        setPassos(newPassos);
        setSelectedStepId(novoPasso.step_number);
    };

    const atualizarPasso = (stepNumber: number, updates: Partial<PassoLocal>) => {
        setPassos(prev => prev.map(p =>
            p.step_number === stepNumber ? { ...p, ...updates } : p
        ));
    };

    const adicionarMensagem = (stepNumber: number) => {
        const currentStep = passos.find(p => p.step_number === stepNumber);
        if (!currentStep || currentStep.messages.length >= MAX_MENSAGENS_POR_PASSO) return;

        const novaMensagem: MensagemLocal = { type: 'text', content: '' };

        setPassos(prev => prev.map(p => {
            if (p.step_number === stepNumber) {
                return { ...p, messages: [...p.messages, novaMensagem] };
            }
            return p;
        }));
    };

    const alterarMensagem = (stepNumber: number, msgIndex: number, field: keyof MensagemLocal, value: any) => {
        setPassos(prev => prev.map(p => {
            if (p.step_number === stepNumber) {
                const newMessages = [...p.messages];
                newMessages[msgIndex] = { ...newMessages[msgIndex], [field]: value };
                // if changing type, reset content if passing isFile check etc (omitted for brevity here, full logic in v2)
                return { ...p, messages: newMessages };
            }
            return p;
        }));
    };

    const deletarMensagem = (stepNumber: number, msgIndex: number) => {
        setPassos(prev => prev.map(p => {
            if (p.step_number === stepNumber) {
                const newMessages = [...p.messages];
                newMessages.splice(msgIndex, 1);
                return { ...p, messages: newMessages };
            }
            return p;
        }));
    };

    const salvarConfig = async () => {
        // Reuse logic from v2 (simplified for brevity)
        setSavedStatus('saving');
        setTimeout(() => setSavedStatus('saved'), 1000);
        setTimeout(() => setSavedStatus(null), 3000);
        // Real implementation would call API
    };

    // Selected Step Data
    const selectedStep = passos.find(p => p.step_number === selectedStepId) || null;

    return (
        <div className={`min-h-screen w-full flex flex-col transition-colors duration-300 ${isDark ? 'bg-[#0f1218] text-gray-200' : 'bg-[#f4f6f8] text-gray-800'
            }`}>

            {/* Header */}
            <header className={`h-16 border-b flex items-center justify-between px-6 sticky top-0 z-30 backdrop-blur-md ${isDark ? 'bg-[#0f1218]/80 border-gray-800' : 'bg-white/80 border-gray-200'
                }`}>
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sminy-primary to-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
                        <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg leading-tight">Studio Follow-Up</h1>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Automação de mensagens</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={salvarConfig}
                        className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all active:scale-95 ${savedStatus === 'saved'
                                ? 'bg-green-500 text-white'
                                : 'bg-sminy-primary hover:bg-sminy-primary-hover text-white shadow-lg shadow-blue-500/20'
                            }`}
                    >
                        {savedStatus === 'saving' ? (
                            <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></div>
                        ) : savedStatus === 'saved' ? (
                            <Check className="w-4 h-4" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {savedStatus === 'saved' ? 'Salvo!' : 'Salvar Alterações'}
                    </button>
                </div>
            </header>

            {/* Main Content - Split Layout */}
            <div className="flex-1 flex overflow-hidden">

                {/* Left Panel: Timeline */}
                <div className={`w-80 border-r flex flex-col ${isDark ? 'border-gray-800 bg-[#161b22]' : 'border-gray-200 bg-white'}`}>
                    <div className="p-4 border-b border-dashed dark:border-gray-800">
                        <h2 className="text-xs font-bold uppercase tracking-wider opacity-50 mb-4">Cronograma ({passos.length})</h2>

                        <div className="space-y-3 relative">
                            {/* Vertical Line */}
                            <div className="absolute left-[19px] top-4 bottom-4 w-[2px] bg-gray-200 dark:bg-gray-800 z-0"></div>

                            {passos.map((passo) => (
                                <div
                                    key={passo.step_number}
                                    onClick={() => setSelectedStepId(passo.step_number)}
                                    className={`relative z-10 group cursor-pointer p-2 rounded-xl border transition-all duration-200 ${selectedStepId === passo.step_number
                                            ? isDark ? 'bg-gray-800 border-gray-700 shadow-md translate-x-1' : 'bg-blue-50 border-blue-200 shadow-md translate-x-1'
                                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-4 transition-colors ${selectedStepId === passo.step_number
                                                ? 'bg-sminy-primary border-white dark:border-gray-800 text-white shadow-lg'
                                                : isDark ? 'bg-gray-800 border-gray-900 text-gray-500' : 'bg-gray-100 border-white text-gray-400'
                                            }`}>
                                            {passo.step_number}
                                        </div>
                                        <div>
                                            <p className={`text-sm font-medium ${selectedStepId === passo.step_number && !isDark ? 'text-blue-900' : ''}`}>
                                                Passo {passo.step_number}
                                            </p>
                                            <p className="text-xs opacity-60">
                                                Após {passo.send_after} {passo.send_after_unit}
                                            </p>
                                        </div>
                                        {selectedStepId === passo.step_number && (
                                            <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                                        )}
                                    </div>
                                </div>
                            ))}

                            {passos.length < MAX_PASSOS && (
                                <button
                                    onClick={adicionarPasso}
                                    className={`w-full py-3 mt-4 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-all group ${isDark
                                            ? 'border-gray-800 text-gray-500 hover:border-gray-700 hover:text-gray-300'
                                            : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600'
                                        }`}
                                >
                                    <div className="w-6 h-6 rounded-full bg-transparent border-2 border-current flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <Plus className="w-3 h-3" />
                                    </div>
                                    <span className="text-xs font-semibold uppercase">Adicionar Passo</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Center Panel: Editor */}
                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    {selectedStep ? (
                        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

                            {/* Header of Step */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold mb-1">Configurar Passo {selectedStep.step_number}</h2>
                                    <p className="text-sm opacity-60">Defina o tempo de envio e mensagens</p>
                                </div>
                                <div className="flex gap-2">
                                    <button className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500'}`}>
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Delay Configuration Card */}
                            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#161b22] border-gray-800' : 'bg-white border-gray-100 shadow-sm'
                                }`}>
                                <div className="flex items-center gap-3 mb-4">
                                    <Clock className="w-5 h-5 text-sminy-primary" />
                                    <h3 className="font-semibold">Atraso de Envio</h3>
                                </div>

                                <div className="flex items-center gap-4">
                                    <span className="text-sm opacity-70">Enviar após</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={selectedStep.send_after}
                                        onChange={(e) => atualizarPasso(selectedStep.step_number, { send_after: Number(e.target.value) })}
                                        className={`w-20 p-2 text-center rounded-xl font-bold text-lg outline-none focus:ring-2 ring-sminy-primary ${isDark ? 'bg-black/20 text-white' : 'bg-gray-100 text-gray-800'
                                            }`}
                                    />
                                    <select
                                        value={selectedStep.send_after_unit}
                                        onChange={(e) => atualizarPasso(selectedStep.step_number, { send_after_unit: e.target.value as any })}
                                        className={`p-2 rounded-xl outline-none focus:ring-2 ring-sminy-primary ${isDark ? 'bg-black/20 text-white' : 'bg-gray-100 text-gray-800'
                                            }`}
                                    >
                                        {timeUnits.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                                    </select>
                                    <span className="text-sm opacity-70">do passo anterior</span>
                                </div>
                            </div>

                            {/* Messages Configuration */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <MessageCircle className="w-5 h-5 text-sminy-primary" />
                                        <h3 className="font-semibold">Mensagens ({selectedStep.messages.length}/3)</h3>
                                    </div>
                                </div>

                                {selectedStep.messages.map((msg, idx) => (
                                    <div key={idx} className={`p-4 rounded-2xl border transition-all ${isDark ? 'bg-[#161b22] border-gray-800' : 'bg-white border-gray-100 shadow-sm'
                                        }`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex items-center gap-2">
                                                <span className="w-6 h-6 rounded-full bg-sminy-primary/10 text-sminy-primary text-xs font-bold flex items-center justify-center">
                                                    {idx + 1}
                                                </span>
                                                <span className="text-sm font-medium">Mensagem</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {messageTypes.map(type => (
                                                    <button
                                                        key={type.value}
                                                        onClick={() => alterarMensagem(selectedStep.step_number, idx, 'type', type.value)}
                                                        className={`p-1.5 rounded-lg transition-all ${msg.type === type.value
                                                                ? 'bg-sminy-primary text-white shadow-md'
                                                                : isDark ? 'bg-gray-800 text-gray-500 hover:bg-gray-700' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                                            }`}
                                                        title={type.label}
                                                    >
                                                        <type.icon className="w-3.5 h-3.5" />
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => deletarMensagem(selectedStep.step_number, idx)}
                                                    className="ml-2 text-red-400 hover:text-red-500"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {msg.type === 'text' ? (
                                            <textarea
                                                rows={3}
                                                value={typeof msg.content === 'string' ? msg.content : ''}
                                                onChange={(e) => alterarMensagem(selectedStep.step_number, idx, 'content', e.target.value)}
                                                placeholder="Olá! Gostaria de saber como você está..."
                                                className={`w-full p-3 rounded-xl resize-none outline-none text-sm transition-colors ${isDark
                                                        ? 'bg-black/20 focus:bg-black/30 text-white placeholder-gray-600'
                                                        : 'bg-gray-50 focus:bg-white border border-transparent focus:border-blue-200 text-gray-800'
                                                    }`}
                                            />
                                        ) : (
                                            <div className={`h-24 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 ${isDark ? 'border-gray-800 bg-black/20' : 'border-gray-200 bg-gray-50'
                                                }`}>
                                                {/* Upload Placeholder */}
                                                <div className="p-2 rounded-full bg-sminy-primary/10 text-sminy-primary">
                                                    {msg.type === 'image' && <Image className="w-5 h-5" />}
                                                    {msg.type === 'video' && <Video className="w-5 h-5" />}
                                                    {msg.type === 'audio' && <Music className="w-5 h-5" />}
                                                </div>
                                                <span className="text-xs opacity-60">Upload {msg.type} feature coming soon</span>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {selectedStep.messages.length < 3 && (
                                    <button
                                        onClick={() => adicionarMensagem(selectedStep.step_number)}
                                        className={`w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 transition-all ${isDark
                                                ? 'border-gray-800 text-gray-500 hover:border-sminy-primary hover:text-sminy-primary hover:bg-sminy-primary/5'
                                                : 'border-gray-200 text-gray-400 hover:border-sminy-primary hover:text-sminy-primary hover:bg-sminy-primary/5'
                                            }`}
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span className="font-medium text-sm">Nova Mensagem</span>
                                    </button>
                                )}
                            </div>

                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center opacity-40">
                            <Layout className="w-16 h-16 mb-4" />
                            <p className="text-lg font-medium">Selecione um passo para editar</p>
                        </div>
                    )}
                </div>

                {/* Right Panel: Preview */}
                <div className={`w-[400px] border-l hidden xl:flex flex-col items-center justify-center p-8 ${isDark ? 'border-gray-800 bg-[#0f1218]' : 'border-gray-200 bg-[#F4F6F8]'
                    }`}>
                    <div className="mb-6 text-center">
                        <h3 className="font-bold mb-1">Preview em Tempo Real</h3>
                        <p className="text-xs opacity-60">Como o paciente verá no WhatsApp</p>
                    </div>

                    <MobilePreview step={selectedStep} />
                </div>

            </div>
        </div>
    );
};

export default FollowUpModern;
